import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GroupAdd, NavigateNext, LocationCity } from '@material-ui/icons';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import {
    handleGetUsersData,
    getModalSwitch,
    setOrderByDirection,
    setOrderByProperty,
    resetState,
    getConfirmModalSwitch,
    resetScroll,
    gridCheckAll,
} from '@app/store/actions';
import {
    getMenuItemById,
    provisionStateSelectData,
    infiniteScrollItemsFetchLimit,
    genericErrorToast,
    emptyGuid,
    defaultOrgId,
    disabled,
} from '@app/helpers';
import {
    ITableHead,
    IListPageBarEnabled,
    IUser,
    IFabMenu,
    IActiveFilter,
    FilterEnum,
    IFilterSelect,
    IDotMenu,
    OrderType,
    Order,
    TranslationsTypes,
    IGroup
} from '@app/models';
import { hasOrgPermission } from '@app/helpers/permissions';
import { ManageSystem, ManageAccount, ReadDirectory, Audit } from '@app/store/constants';
import { basicService, groupsService, usersService } from '@app/services';
import {
    IModalData,
    ModalComponent,
    FabMenu,
    IMenuItem,
    mainMenuTypes,
    Confirmation,
    PasslyTable
} from '@app/components';
import { confirm } from '@app/components/Modal/confirmModal';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import AddUserModalForm from '@app/pages/directoryManager/dialogs/addUser.dialog';
import ImportUsersModalForm from '@app/pages/directoryManager/dialogs/importUsersModalForm';
import UpdateUsersDialog from '@app/pages/directoryManager/dialogs/updateUsers.dialog';
import { TableCell } from '@material-ui/core';

interface IUsersTableData {
    checked: boolean;
    checkboxDisabled?: boolean;
    principalName: string;
    displayName: string;
    lastSignedIn: Date;
    syncSourceName: string;
    syncSource: string;
    applicationSyncSource: string;
    applicationSyncSourceName: string;
    provisioningState: string;
    emailAddress: string;
    id: string;
    cantDelete?: boolean;
}

enum TableHeadEnum {
    displayName = 'displayName',
    emailAddress = 'emailAddress',
    lastSignedIn = 'lastSignedIn',
    syncSourceName = 'syncSourceName',
    applicationSyncSourceName = 'applicationSyncSourceName',
    provisioningState = 'provisioningState',
    groups = 'groups'
}

const UsersPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Users);
    const userInfiniteScroll = infiniteScrollItemsFetchLimit;

    const confirmModalState = useSelector((state: any) => state.modal.confirmModalSwitcher);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const users = useSelector((state: any) => state.users.users);
    const currentUserId = useSelector((state: any) => state.signIn.currentUser.userUuid);
    const usersTotalCount = useSelector((state: any) => state.users.count);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const [groups, setGroups] = useState<IGroup[]>([]);

    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IUsersTableData[]>([]);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const [groupFilterData, setGroupFilterData] = useState<IFilterSelect[]>([]);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const dialogText = `${translation('IMPORT_QUEUED_COUNT_SECOND')} ${translation('IMPORT_QUEUED_COUNT_NOTIFY')}`;

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: hasOrgPermission([ManageAccount])
    };

    const editRow = (e: any) => {
        basicService.redirect('/directory/users/details/' + e.currentTarget.dataset.id);
    };

    const getComplexSyncSourceNameCell = (row: any) => {
        return (
            <TableCell>
                <span>{row.syncSourceName}</span>
                <div>{row.syncSource !== defaultOrgId ? '(' + translation('SYNC_SOURCE_AD') + ')' : ''}</div>
            </TableCell>
        );
    };

    const getComplexAppSyncSourceNameCell = (row: any) => {
        return (
            <TableCell>
                <span>{row.applicationSyncSourceName}</span>
                <div>{row.applicationSyncSource !== defaultOrgId ? '(' + translation('SYNC_SOURCE_AAD') + ')' : ''}</div>
            </TableCell>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.displayName,
            label: 'TABLE_USER_TITLE',
            type: 'linkWithTooltip',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.displayName,
            onClick: editRow
        },
        {
            id: TableHeadEnum.emailAddress,
            label: 'TABLE_EMAIL_TITLE',
            type: 'withTooltip',
            filterType: FilterEnum.contains
        },
        {
            id: TableHeadEnum.lastSignedIn,
            label: 'USERS_LAST_LOGON',
            type: 'dateTime'
        },
        {
            id: TableHeadEnum.syncSourceName,
            label: 'SYNC_SOURCE',
            type: 'getComplexCell',
            getComplexCell: getComplexSyncSourceNameCell
        },
        {
            id: TableHeadEnum.applicationSyncSourceName,
            label: 'APP_SYNC_SOURCE',
            type: 'getComplexCell',
            getComplexCell: getComplexAppSyncSourceNameCell
        },
        {
            id: TableHeadEnum.provisioningState,
            label: 'USERS_STATE',
            type: 'status',
            filterType: FilterEnum.provisioningState,
            suggestionsForFilter: provisionStateSelectData
        },
        {
            id: TableHeadEnum.groups,
            label: 'GROUPS',
            type: 'text',
            filterType: FilterEnum.groups,
            suggestionsForFilter: groupFilterData,
            hidden: true
        }
    ];

    const menu: IFabMenu = {
        name: '', // TODO: MENU_ITEM_SAFE ?
        actions: [
            {
                name: 'NEW_USER',
                method: () => addNewUser(),
                iconElement: <i className='icon icon-users' />
            },
            {
                name: 'NEW_USER_BULK',
                method: () => importUsers(),
                iconElement: <CloudUploadIcon />
            }
        ]
    };

    useEffect(() => {
        if (groups?.length) {
            setGroupFilterData(
                groups.map((group: IGroup, index: number) => {
                    const data: IFilterSelect = {
                        id: index,
                        label: group.Name,
                        value: group.Id
                    };
                    return data;
                })
            );
        }
    }, [groups]);

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    const handleGroupsList = (response: any) => {
        const results: IGroup[] = response;

        if (results) {
            setGroups(results);
        }
    };

    useEffect(() => {
        if (organizationFilter !== undefined) {
            groupsService.getAllGroupsDTOInitial(Order.asc, organizationFilter).then(handleGroupsList);
        }
    }, [organizationFilter]);

    useEffect(() => {
        if (!loadingData) {
            let newRows = users.map((user: IUser) => {
                const data: IUsersTableData = {
                    checked: false,
                    checkboxDisabled: user.Id === currentUserId,
                    displayName: user.DisplayName,
                    principalName: user.PrincipalName,
                    lastSignedIn: user.LastSignedIn,
                    syncSourceName: user.SyncSourceName,
                    syncSource: user.SyncSource,
                    applicationSyncSource: user.ApplicationSyncSource,
                    applicationSyncSourceName: user.ApplicationSyncSourceName,
                    provisioningState: user.ProvisioningState === disabled ? translation('USER_INACTIVE') : user.ProvisioningState,
                    cantDelete: user.SyncSource !== defaultOrgId,
                    id: user.Id,
                    emailAddress: user.EmailAddress
                };
                return data;
            });

            if (users.length === 0) {
                setRows([]);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    }, [loadingData, users]);

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (loadingData) {
            return;
        }
        dispatch(handleGetUsersData(orderBy, order, userInfiniteScroll, skipCount, organizationFilter, isReset, activeFilters));
    };

    const addNewUser = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_USER'),
            modalSwitcher: modalSwitchState,
            form: <AddUserModalForm handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const importUsers = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_USER_BULK'),
            modalSwitcher: modalSwitchState,
            form: <ImportUsersModalForm />
        });
    };

    const emptyUserList = () => {
        return !loadingData && rows.length === 0;
    };

    const iconsConfirmModal = (
        <div className='confirmModal__body-icons'>
            <GroupAdd />
            <NavigateNext />
            <LocationCity />
        </div>
    );

    const updateSelected = useCallback(() => {
        // loop thru rows.checked
        let checkedUsers: IUsersTableData[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                checkedUsers.push(row);
            }
        });

        if (checkedUsers.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('UPDATE_MANY_USERS_STATE_HEADER'),
            modalSwitcher: modalSwitchState,
            form: (
                <UpdateUsersDialog
                    usersList={checkedUsers}
                    usersListId={checkedUsers.map((user) => user.id)}
                    updateManyCallback={handlePageRefreshFromDialog}
                />
            )
        });
    }, [rows]);

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];
        let unsyncedIds: string[] = [];
        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);

                if (row.syncSource === emptyGuid) {
                    unsyncedIds.push(row.id);
                }
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        const message: TranslationsTypes = ids.length > 1 ? 'DELETE_USER_BODY' : 'DELETE_SINGLE_USER_BODY';
        if (unsyncedIds.length !== ids.length) {
            deleteUsersNoSyncAreYouSure(ids, unsyncedIds, message);
        } else {
            deleteUsersAreYouSure(ids, message);
        }
    }, [rows]);

    const deleteUsersAreYouSure = (ids: string[], messageType: TranslationsTypes) => {
        confirm(translation(messageType), translation('DELETE'), translation('CANCEL'), translation('DELETE_USER_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteUsers(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteUsersNoSyncAreYouSure = (ids: string[], unsyncedIds: string[], messageType: TranslationsTypes) => {
        if (unsyncedIds.length === 0) {
            confirm(translation('SELECT_MORE_SYNC'), translation('OK'), undefined, translation('DELETE_USER_HEADER'), false, {
                isDeleteAction: true
            }).then(
                () => {
                    // do nothing on ok
                },
                () => {
                    // do nothing on cancel
                }
            );
        } else {
            confirm(translation(messageType), translation('DELETE'), translation('CANCEL'), translation('DELETE_USER_HEADER'), true, {
                isDeleteAction: true
            }).then(
                () => {
                    deleteUsers(unsyncedIds);
                },
                () => {
                    // do nothing on cancel
                }
            );
        }
    };

    const detachSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        disconnectUsersAreYouSure(ids);
    }, [rows]);

    const disconnectUsersAreYouSure = (ids: any) => {
        confirm(translation('DETACH_PEOPLE_BODY'), translation('DETACH'), translation('CANCEL'), translation('DETACH_PEOPLE_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                disconnectUsers(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('UPDATE_SELECTED'),
            action: updateSelected
        },
        {
            name: translation('DETACH_SELECTED'),
            action: detachSelected
        },
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const deleteUsers = (ids: string[]) => {
        if (ids.length > 0) {
            // if the id happens to be the current user, remove it
            const idsWithoutCurrentUser = ids.filter((item) => item !== currentUserId);

            if (idsWithoutCurrentUser.length > 0) {
                let users: any = {
                    People: ids
                };

                usersService.deleteUsers(users, genericErrorToast, handleDeleteUsersSuccess);
            }
        }
    };

    const handleDeleteUsersSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
            dispatch(gridCheckAll(false));
        }
    };

    const disconnectUsers = (ids: string[]) => {
        if (ids.length > 0) {
            // if the id happens to be the current user, remove it
            const idsWithoutCurrentUser = ids.filter((item) => item !== currentUserId);

            if (idsWithoutCurrentUser.length > 0) {
                let users: any = {
                    People: ids
                };

                usersService.disconnectUsers(users, genericErrorToast, handleDeleteUsersSuccess);
            }
        }
    };

    const deleteRow = (e: any) => {
        const chosenId: string = e.currentTarget.dataset.id;

        if (chosenId !== currentUserId) {
            let ids: string[] = [];
            ids.push(e.currentTarget.dataset.id);

            deleteUsersAreYouSure(ids, 'DELETE_SINGLE_USER_BODY');
        }
    };

    const closeConfirmModal = () => dispatch(getConfirmModalSwitch(false));

    return (
        <>
            {confirmModalState && (
                <Confirmation
                    titleLabel={translation('IMPORT_QUEUED')}
                    dialogText={dialogText}
                    cancelLabel=''
                    proceedLabel={translation('OK')}
                    proceed={closeConfirmModal}
                    icons={iconsConfirmModal}
                />
            )}
            <ModalComponent {...modalData} />

            {!loadingData && hasOrgPermission([ManageAccount, ManageSystem]) && (
                <FabMenu
                    menu={menu}
                    hideTooltip={!emptyUserList()}
                    tooltipTitle={translation('USERS_NO_USERS_TOOL_TIP_TITLE')}
                    tooltipDescription={translation('USERS_NO_USERS_TOOL_TIP_DESC')}
                />
            )}

            {!loadingData && !hasOrgPermission([ReadDirectory, ManageSystem, ManageAccount, Audit]) ? (
                <div className='table--empty'>{translation('NO_ORG_USER_PERMISSIONS')}</div>
            ) : (
                <PasslyTable
                    rows={rows}
                    setRows={setRows}
                    order={order}
                    orderBy={orderBy}
                    handleOrderChange={createSortHandler}
                    headerItems={headCells}
                    rowsCount={usersTotalCount}
                    onFetch={fetchMoreData}
                    withCheckboxes={hasOrgPermission([ManageAccount])}
                    actionMenuItems={actionMenuItems}
                    emptyListHelper={translation('EMPTY_USER_LIST')}
                    loadingData={loadingData}
                    editRow={editRow}
                    deleteRow={deleteRow}
                />
            )}
        </>
    );
};

export default UsersPage;
